// Per-browser settings (the apps' per-phone settings; nothing here syncs). Defaults match the apps.
import { Hub } from "./util";

export type VoiceProvider = "gemini" | "openrouter" | "openai";
export type Appearance = "system" | "light" | "dark";
export type LibrarySort = "lastOpened" | "added";
export type DecisionsBackend = "auto" | "openai" | "jev";

const DEFAULTS = {
  model: "deepseek/deepseek-v4.1-flash",
  spoilerCheck: true,
  prefetchNotes: true,
  textSize: 2,
  appearance: "system" as Appearance,
  hasSeenCoach: false,
  librarySort: "lastOpened" as LibrarySort,
  askEnabled: true,
  askModel: "google/gemini-3.8-flash",
  askSpeak: true,
  askResume: true,
  decisionsBackend: "auto" as DecisionsBackend,
  provider: "gemini" as VoiceProvider,
  geminiModel: "gemini-3.8-flash-tts",
  voiceName: "Charon",
  openRouterModel: "hexgrad/kokoro-82m",
  openRouterVoice: "af_heart",
  openAIModel: "gpt-realtime-2.1-mini",
  openAIVoice: "marin",
  narrationStyle: "",
  useFlex: true,
  playbackRate: 1,
  autoTag: true,
};

export type Settings = typeof DEFAULTS;
const KEY = "depth.settings";

function load(): Settings {
  try { return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(KEY) || "{}") }; } catch { return { ...DEFAULTS }; }
}

export const settings: Settings = load();
export const settingsEvents = new Hub<{ changed: keyof Settings }>();

export function set<K extends keyof Settings>(key: K, value: Settings[K]): void {
  settings[key] = value;
  try { localStorage.setItem(KEY, JSON.stringify(settings)); } catch { /* ignore */ }
  settingsEvents.emit("changed", key);
}

export const GEMINI_MODELS = [
  { id: "gemini-3.8-flash-tts", label: "Gemini 3.8 Flash TTS" },
  { id: "gemini-3.8-flash-lite-tts", label: "Gemini 3.8 Flash-Lite TTS" },
];

export const GEMINI_VOICES: [string, string][] = [
  ["Zephyr", "Bright"], ["Puck", "Upbeat"], ["Charon", "Informative"], ["Kore", "Firm"], ["Fenrir", "Excitable"],
  ["Leda", "Youthful"], ["Orus", "Firm"], ["Aoede", "Breezy"], ["Callirrhoe", "Easy-going"], ["Autonoe", "Bright"],
  ["Enceladus", "Breathy"], ["Iapetus", "Clear"], ["Umbriel", "Easy-going"], ["Algieba", "Smooth"], ["Despina", "Smooth"],
  ["Erinome", "Clear"], ["Algenib", "Gravelly"], ["Rasalgethi", "Informative"], ["Laomedeia", "Upbeat"], ["Achernar", "Soft"],
  ["Alnilam", "Firm"], ["Schedar", "Even"], ["Gacrux", "Mature"], ["Pulcherrima", "Forward"], ["Achird", "Friendly"],
  ["Zubenelgenubi", "Casual"], ["Vindemiatrix", "Gentle"], ["Sadachbia", "Lively"], ["Sadaltager", "Knowledgeable"], ["Sulafat", "Warm"],
];

export const OPENAI_VOICE_MODELS = [
  { id: "gpt-realtime-2.1-mini", label: "GPT Realtime 2.1 mini", hour: "≈ $1.50/hour" },
  { id: "gpt-realtime-2.1", label: "GPT Realtime 2.1", hour: "≈ $4.80/hour" },
];
export const OPENAI_VOICES = ["marin", "cedar", "alloy", "ash", "ballad", "coral", "echo", "sage", "shimmer", "verse"];

export const NARRATION_STYLES: [string, string][] = [
  ["Off", ""],
  ["Audiobook", "a calm, warm audiobook narrator, at a relaxed pace with natural pauses"],
  ["Podcast host", "a friendly, conversational podcast host, engaged and natural"],
  ["Brisk news", "a brisk, clear news anchor"],
];

/** Who narrates: provider + model + voice; recorded on an article as modelName "<provider>:<model>" + voiceName. */
export interface NarrationVoice { provider: VoiceProvider; model: string; voice: string }

export function currentVoice(): NarrationVoice {
  switch (settings.provider) {
    case "openrouter": return { provider: "openrouter", model: settings.openRouterModel, voice: settings.openRouterVoice };
    case "openai": return { provider: "openai", model: settings.openAIModel, voice: settings.openAIVoice };
    default: return { provider: "gemini", model: settings.geminiModel, voice: settings.voiceName };
  }
}

export function decodeVoice(modelName: string | null | undefined, voiceName: string | null | undefined): NarrationVoice | null {
  if (!modelName) return null;
  if (modelName.startsWith("openrouter:")) return { provider: "openrouter", model: modelName.slice(11), voice: voiceName ?? "" };
  if (modelName.startsWith("openai:")) return { provider: "openai", model: modelName.slice(7), voice: voiceName ?? "" };
  return { provider: "gemini", model: modelName.startsWith("gemini:") ? modelName.slice(7) : modelName, voice: voiceName ?? "" };
}

export const encodeVoice = (v: NarrationVoice) => `${v.provider}:${v.model}`;

export function voiceLabel(v: NarrationVoice): string {
  const p = v.provider === "gemini" ? "Gemini" : v.provider === "openai" ? "OpenAI" : "OpenRouter";
  return `${p} · ${v.model} · ${v.voice || "default voice"}`;
}
